import { LineChart, PieChart } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatedView } from '@unistocks/components/basic/animated-view';
import { Column } from '@unistocks/components/layout/flex';
import { HeightAnimator, HeightAnimatorProps } from '@unistocks/components/layout/height-animator';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { RenderTab, Tab, Tabs } from '@unistocks/components/tabs/tabs';
import { PortfolioValueSummary } from '@unistocks/features/portfolios/components/portfolio-details/portfolio-value-summary';
import {
  PortfolioActions,
  selectPieChartType
} from '@unistocks/features/portfolios/store/portfolio.slice';
import {
  BucketDto,
  ExternalAssetSearchDto,
  PieChartType,
  PortfolioComparisonSettingsDto,
  PortfolioDto
} from '@unistocks/proxy/axios-client';
import {
  setPortfoliosGetData,
  usePortfoliosUpdateMutation
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import { MediumTransition } from '@unistocks/shared/animations';
import { includeAllFilter } from '@unistocks/shared/filter';
import { useAppDispatch, useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { DateRange, Filter } from '@unistocks/types';
import React, { Fragment, useState } from 'react';
import { isWeb } from 'tamagui';

const PortfolioPieChart = React.lazy(
  () => import('@unistocks/features/portfolios/components/charts/pie-chart/pie-chart')
);

const PortfolioLineChart = React.lazy(
  () => import('@unistocks/features/portfolios/components/charts/line-chart/line-chart')
);

export type PortfolioTabsProps = {
  portfolio: PortfolioDto;
  portfolioHistory?: BucketDto;
  isLoadingPortfolioHistory: boolean;
  todaysChange?: BucketDto;
  todaysChangeLoading?: boolean;
  assetFilter: Filter<string>;
  setAssetFilter: React.Dispatch<React.SetStateAction<Filter<string>>>;
  sectionFilter: Filter<string>;
  setSectionFilter: React.Dispatch<React.SetStateAction<Filter<string>>>;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
};

export const PortfolioTabs: React.FC<PortfolioTabsProps> = ({
  portfolio,
  portfolioHistory,
  isLoadingPortfolioHistory,
  todaysChange,
  todaysChangeLoading,
  assetFilter,
  setAssetFilter,
  sectionFilter,
  setSectionFilter,
  setDateRange
}) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const pieChartType = useAppSelector(selectPieChartType(portfolio.id));
  const [activeTab, setActiveTab] = useState('pie-chart');
  const { isReadonly } = portfolio;

  const { mutate: updatePortfolio } = usePortfoliosUpdateMutation(portfolio.id, {
    onSuccess: data => {
      setPortfoliosGetData(queryClient, () => data, portfolio.id);
    }
  });

  const handleComparisonIndexChange = (index?: ExternalAssetSearchDto) => {
    const comparisonSettings: PortfolioComparisonSettingsDto | null = index
      ? {
          index,
          sharedPortfolioId: portfolio.settings.comparisonSettings?.sharedPortfolioId ?? null
        }
      : portfolio.settings.comparisonSettings?.sharedPortfolioId
        ? {
            index: null,
            sharedPortfolioId: portfolio.settings.comparisonSettings?.sharedPortfolioId ?? null
          }
        : null;

    updatePortfolio({
      name: portfolio.name,
      tableColumns: null,
      portfolioComparisonSettings: comparisonSettings
    });
  };

  const handleComparisonSharedPortfolioChange = (sharedPortfolioId?: string) => {
    const comparisonSettings: PortfolioComparisonSettingsDto | null = sharedPortfolioId
      ? {
          index: portfolio.settings.comparisonSettings?.index || null,
          sharedPortfolioId: sharedPortfolioId
        }
      : portfolio.settings.comparisonSettings?.index?.identifier
        ? {
            index: portfolio.settings.comparisonSettings?.index || null,
            sharedPortfolioId: null
          }
        : {
            index: null,
            sharedPortfolioId: null
          };

    updatePortfolio({
      name: portfolio.name,
      tableColumns: null,
      portfolioComparisonSettings: comparisonSettings
    });
  };

  const tabs: Tab[] = [{ icon: <PieChart />, value: 'pie-chart' }];

  if (portfolioHistory) tabs.push({ icon: <LineChart />, value: 'line-chart' });

  const onPieChartModeChange = (mode: PieChartType) => {
    dispatch(PortfolioActions.setPortfolioPieChartType({ id: portfolio.id, pieChart: mode }));
    setAssetFilter(includeAllFilter as Filter<string>);
    setSectionFilter(includeAllFilter as Filter<string>);
  };

  const renderTab: RenderTab = tab => {
    switch (tab) {
      case 'pie-chart':
        return (
          <React.Suspense fallback={<Loader />}>
            <Column $lg={{ fd: 'row-reverse', jc: 'space-between', ai: 'center', gap: '$3' }}>
              <PortfolioPieChart
                mode={pieChartType}
                onModeChange={onPieChartModeChange}
                portfolio={portfolio!}
                assetFilter={assetFilter}
                setAssetFilter={setAssetFilter}
                sectionFilter={sectionFilter}
                setSectionFilter={setSectionFilter}
                fd={'column'}
                gap={'$3'}
                $sm={{ alignItems: 'center' }}
                $lg={{
                  flex: 1
                }}
                chartContainerProps={{
                  h: '$20',
                  w: '100%',
                  $lg: {
                    h: 350
                  }
                }}
                legendContainerProps={{
                  maxH: '$16',
                  $sm: {
                    w: 400
                  }
                }}
              />
              <AnimatedView
                layout={MediumTransition}
                fd={'row'}
                justifyContent="center"
                $lg={{ flex: 1 }}
              >
                <PortfolioValueSummary
                  portfolio={portfolio}
                  todaysChange={todaysChange}
                  isTodaysChangeFetching={todaysChangeLoading}
                  assetFilter={assetFilter}
                  sectionFilter={sectionFilter}
                  mt={'$5'}
                  w={'100%'}
                  $sm={{ maxW: 400 }}
                />
              </AnimatedView>
            </Column>
          </React.Suspense>
        );

      case 'line-chart':
        return (
          <React.Suspense fallback={<Loader />}>
            <PortfolioLineChart
              portfolio={portfolio}
              portfolioHistory={portfolioHistory}
              isLoading={isLoadingPortfolioHistory}
              onDateRangeChange={setDateRange}
              showChartSettingsMenu={!isReadonly}
              defaultIndex={portfolio.settings.comparisonSettings?.index ?? undefined}
              defaultSharedPortfolioId={
                portfolio.settings.comparisonSettings?.sharedPortfolioId ?? undefined
              }
              onComparisonIndexChange={handleComparisonIndexChange}
              onComparisonSharedPortfolioChange={handleComparisonSharedPortfolioChange}
              chartContainerProps={{
                h: 400,
                $sm: { h: 400 },
                $md: { h: 500 }
              }}
            />
          </React.Suspense>
        );
    }
  };

  const Wrapper = isWeb ? HeightAnimator : Fragment;
  const props: HeightAnimatorProps = isWeb ? { animateFromTop: true, initial: false } : {};

  return (
    <Wrapper {...props}>
      <Tabs
        tabs={tabs}
        value={activeTab}
        hideSingleTab
        onValueChange={setActiveTab}
        renderTab={renderTab}
      />
    </Wrapper>
  );
};
